import { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useUser } from '~/api/user';
import { Button, Dialog } from '~/components';
import { SearchParamModals } from '~/constants/url';
import { useShouldOpenModal } from '~/hooks/useShouldOpenModal';
import { clsx } from '~/styles/utils';
import { isPhotographer } from '~/utils/user';

import type { SectionActionsProps as Props } from './SectionActions.types';

import { useFormWithLinesContext } from '../FormLines.context';
import { nestedRowStyle, rowGridStyle, tableSectionStyle } from '../FormLines.css';
import { createLine, createPhotoAlbumSection, createTextLine } from '../FormLines.utils';
import { useSectionContext } from '../Section/Section.context';
import { ProductPickerDialog } from './ProductPickerDialog/ProductPickerDialog';
import { indentedStyle, photoAlbumSectionStyle, rowStyle } from './SectionActions.css';

export const SectionActions = ({ disableEntryLines = false, i18nNamespace }: Props) => {
  const { t } = useTranslation(['lines']);

  const { data: user } = useUser();
  const {
    clearErrors,
    formState: { isSubmitted },
  } = useFormWithLinesContext();
  const {
    linesFieldArray,
    mobileLineReducer: [, expandMobileLine],
    section,
    sectionIndex,
    sectionsFieldArray,
  } = useSectionContext();

  const { fields: sections, prepend: prependSection } = sectionsFieldArray;
  const { append: appendLine, fields: lines } = linesFieldArray;

  const productPickerDialogHandle = Dialog.useHandle();
  const productPickerTriggerId = useId();

  const shouldOpenModal = useShouldOpenModal(SearchParamModals.ADD_PRODUCT);

  // Limit to one photo album section
  const canAddPhotoAlbumSection = isPhotographer(user) && !section.isPhotoAlbum && !sections.some((section) => section.isPhotoAlbum);

  /**
   * Add an empty line to the section
   */
  const onAddLine = useCallback(() => {
    expandMobileLine({ sectionIndex, lineIndex: lines.length });
    appendLine(createLine());

    // Clear the sections error as appending a line doesn't retrigger the validation.
    // As a side effect, this clears all errors of each line as well, which is acceptable behavior.
    if (isSubmitted) clearErrors('sections');
  }, [appendLine, clearErrors, expandMobileLine, isSubmitted, lines.length, sectionIndex]);

  /**
   * Add a photo album section with an empty line
   */
  const onAddPhotoAlbum = useCallback(() => {
    prependSection(createPhotoAlbumSection(!disableEntryLines));
    expandMobileLine({ sectionIndex: 0, lineIndex: 0 });
  }, [disableEntryLines, expandMobileLine, prependSection]);

  /**
   * Add a text-only line
   */
  const onAddTextLine = useCallback(() => {
    expandMobileLine({ sectionIndex, lineIndex: lines.length });
    appendLine(createTextLine());
  }, [appendLine, expandMobileLine, lines.length, sectionIndex]);

  return (
    <tbody className={clsx(tableSectionStyle, section.isPhotoAlbum && photoAlbumSectionStyle)}>
      <tr className={rowGridStyle}>
        <td className={nestedRowStyle}>
          <div className={clsx(rowStyle, section.isPhotoAlbum && indentedStyle)}>
            <Dialog.Trigger
              handle={productPickerDialogHandle}
              id={productPickerTriggerId}
              render={
                <Button emphasis="primary" icon="Add">
                  {t('lines:new.product')}
                </Button>
              }
            />

            {!disableEntryLines && (
              <Button emphasis="secondary" icon="Add" onClick={onAddLine}>
                {section.isPhotoAlbum
                  ? t('lines:new.photoAlbumLine')
                  : match(i18nNamespace)
                      .with('invoices', () => t('lines:new.invoiceLine'))
                      .with('quotations', () => t('lines:new.quotationLine'))
                      .exhaustive()}
              </Button>
            )}

            {canAddPhotoAlbumSection && (
              <Button emphasis="secondary" icon="Add" onClick={onAddPhotoAlbum}>
                {t('lines:new.photoAlbum')}
              </Button>
            )}

            <Button emphasis="secondary" icon="Add" onClick={onAddTextLine}>
              {t('lines:new.textLine')}
            </Button>
          </div>
        </td>
      </tr>

      <Dialog.Root
        defaultOpen={shouldOpenModal && !section.isPhotoAlbum}
        defaultTriggerId={productPickerTriggerId}
        handle={productPickerDialogHandle}
      >
        <ProductPickerDialog onClose={() => productPickerDialogHandle.close()} />
      </Dialog.Root>
    </tbody>
  );
};
